import React, { useEffect, useRef, useState } from 'react'
import { Box, styled, CircularProgress } from '@mui/material'

const Wrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexShrink: 0,
  paddingBottom: 3,
  position: 'relative',
})

const GlassBtn = styled('button', {
  shouldForwardProp: (p) => p !== '$solid' && p !== '$pending',
})<{ $solid?: boolean; $pending?: boolean }>(({ $solid, $pending }) => ({
  height: 42,
  padding: '0 22px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 13.5,
  fontWeight: 650,
  whiteSpace: 'nowrap',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 140ms ease',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  ...($solid
    ? { background: 'var(--ds-canvas)', color: 'var(--ds-ink)', boxShadow: 'none' }
    : {
        background: 'color-mix(in srgb, var(--ds-night-ink) 12%, transparent)',
        color: $pending ? 'var(--ds-night-ink-muted)' : 'var(--ds-night-ink)',
        boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--ds-night-ink) 22%, transparent)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }),
  '&:hover': $solid
    ? { opacity: 0.9, background: 'var(--ds-canvas)' }
    : { background: 'color-mix(in srgb, var(--ds-night-ink) 20%, transparent)' },
  '&:disabled': { opacity: 0.6, cursor: 'default' },
}))

const MenuDot = styled('div')({
  width: 42,
  height: 42,
  borderRadius: '50%',
  background: 'color-mix(in srgb, var(--ds-night-ink) 12%, transparent)',
  boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--ds-night-ink) 22%, transparent)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--ds-night-ink)',
  fontSize: 17,
  cursor: 'pointer',
  flexShrink: 0,
  position: 'relative',
  transition: 'background 140ms ease',
  '&:hover': { background: 'color-mix(in srgb, var(--ds-night-ink) 20%, transparent)' },
})

const DropMenu = styled(Box, {
  shouldForwardProp: (p) => p !== '$open',
})<{ $open: boolean }>(({ $open }) => ({
  position: 'absolute',
  top: 50,
  right: 0,
  background: 'var(--ds-canvas)',
  borderRadius: 10,
  boxShadow: 'var(--ds-shadow-deep)',
  padding: 5,
  minWidth: 180,
  zIndex: 20,
  opacity: $open ? 1 : 0,
  visibility: $open ? 'visible' : 'hidden',
  transform: $open ? 'translateY(0)' : 'translateY(-4px)',
  transition: 'all 140ms ease',
  textAlign: 'left',
}))

const MenuItem = styled('button', {
  shouldForwardProp: (p) => p !== '$danger',
})<{ $danger?: boolean }>(({ $danger }) => ({
  display: 'flex',
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  borderRadius: 7,
  fontSize: 12.5,
  fontWeight: 550,
  color: $danger ? 'var(--ds-ink-muted)' : 'var(--ds-ink-muted)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  '&:hover': $danger
    ? { background: 'color-mix(in srgb, var(--ds-accent-deep-a, #e97a4a) 10%, transparent)', color: 'var(--ds-accent-deep-a, #e97a4a)' }
    : { background: 'var(--ds-hover)', color: 'var(--ds-ink)' },
}))

export type ConnectState = 'none' | 'sent' | 'received' | 'connected'

export interface ProfileHeroActionsProps {
  isSelf: boolean
  connectState: ConnectState
  onConnect?: () => void
  onAccept?: () => void
  onDecline?: () => void
  onMessage?: () => void
  onEditProfile?: () => void
  onViewConnections?: () => void
  onMoveToCircle?: () => void
  onRemoveConnection?: () => void
  onBlock?: () => void
  onCancelRequest?: () => void
  loading?: boolean
}

export function ProfileHeroActions({
  isSelf,
  connectState,
  onConnect,
  onAccept,
  onDecline,
  onMessage,
  onEditProfile,
  onViewConnections,
  onMoveToCircle,
  onRemoveConnection,
  onBlock,
  onCancelRequest,
  loading,
}: ProfileHeroActionsProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [menuOpen])

  const spinner = loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null

  const menu = (items: { label: string; onClick?: () => void; danger?: boolean }[]) => (
    <MenuDot onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o) }}>
      ⋯
      <DropMenu $open={menuOpen}>
        {items.map((item) => (
          <MenuItem key={item.label} $danger={item.danger} onClick={item.onClick}>
            {item.label}
          </MenuItem>
        ))}
      </DropMenu>
    </MenuDot>
  )

  if (isSelf) {
    return (
      <Wrap ref={wrapRef}>
        <GlassBtn $solid onClick={onEditProfile}>Edit profile</GlassBtn>
        <GlassBtn onClick={onViewConnections}>Connections</GlassBtn>
      </Wrap>
    )
  }

  if (connectState === 'connected') {
    return (
      <Wrap ref={wrapRef}>
        <GlassBtn $solid>✓ Connected</GlassBtn>
        <GlassBtn onClick={onMessage}>Message</GlassBtn>
        {menu([
          { label: 'Move to circle…', onClick: onMoveToCircle },
          { label: 'Remove connection', onClick: onRemoveConnection, danger: true },
          { label: 'Block', onClick: onBlock, danger: true },
        ])}
      </Wrap>
    )
  }

  if (connectState === 'sent') {
    return (
      <Wrap ref={wrapRef}>
        <GlassBtn $pending disabled={loading}>{spinner}Pending</GlassBtn>
        {menu([{ label: 'Cancel request', onClick: onCancelRequest, danger: true }])}
      </Wrap>
    )
  }

  if (connectState === 'received') {
    return (
      <Wrap ref={wrapRef}>
        <GlassBtn $solid onClick={onAccept} disabled={loading}>{spinner}Accept</GlassBtn>
        <GlassBtn onClick={onDecline}>Decline</GlassBtn>
      </Wrap>
    )
  }

  return (
    <Wrap ref={wrapRef}>
      <GlassBtn $solid onClick={onConnect} disabled={loading}>{spinner}Connect</GlassBtn>
      {menu([{ label: 'Block', onClick: onBlock, danger: true }])}
    </Wrap>
  )
}
